import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Fade
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, ArrowBackIos, ArrowForwardIos, Delete as DeleteIcon } from '@mui/icons-material';
import { api } from '../utils/api';

const NoteDialog = ({ 
  open, 
  handleClose, 
  onSave, 
  noteToEdit = null,
  showNavigation = false,
  currentIndex = 0,
  totalNotes = 0,
  onNavigate = null,
  selectedDate = null
}) => {
  const [loading, setLoading] = useState(false);
  
  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [formData, setFormData] = useState({
    title: '',
    date: formatLocalDate(new Date()), // Today's date in YYYY-MM-DD format
    content: ''
  });
  const [errors, setErrors] = useState({});

  // Reset form when dialog opens/closes or when note changes
  useEffect(() => {
    if (open) {
      if (noteToEdit) {
        // Editing existing note or creating with preset date
        setFormData({
          title: noteToEdit.title || '',
          date: noteToEdit.date || (selectedDate ? formatLocalDate(selectedDate) : formatLocalDate(new Date())),
          content: noteToEdit.content || ''
        });
      } else {
        // Creating new note
        setFormData({
          title: '',
          date: selectedDate ? formatLocalDate(selectedDate) : formatLocalDate(new Date()),
          content: ''
        });
      }
      setErrors({});
    }
  }, [open, noteToEdit, selectedDate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }

    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = 'Il titolo è obbligatorio';
    } else if (formData.title.length > 80) {
      newErrors.title = 'Il titolo non può superare i 80 caratteri';
    }

    // Date validation
    if (!formData.date) {
      newErrors.date = 'La data è obbligatoria';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      let response;
      
      if (noteToEdit && noteToEdit.id) {
        // Update existing note
        response = await api.put(`/notes/${noteToEdit.id}`, formData);
      } else {
        // Create new note
        response = await api.post('/notes', formData);
      }

      if (response.data.success) {
        onSave(response.data.note);
        handleClose();
      } else {
        alert(response.data.message || 'Errore nel salvare la nota');
      }
    } catch (err) {
      console.error('Error saving note:', err);
      alert(`Errore nel salvare la nota: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    handleClose();
  };

  const handleDelete = async () => {
    if (!noteToEdit || !noteToEdit.id) return;
    
    if (!window.confirm('Sei sicuro di voler eliminare questa nota?')) {
      return;
    }

    setLoading(true);
    try {
      const response = await api.delete(`/notes/${noteToEdit.id}`);
      
      if (response.data.success) {
        onSave(); // Refresh the notes list
        handleClose();
      } else {
        alert(response.data.message || 'Errore nell\'eliminare la nota');
      }
    } catch (err) {
      console.error('Error deleting note:', err);
      alert(`Errore nell'eliminare la nota: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showNavigation && (
              <IconButton 
                onClick={() => onNavigate(-1)}
                size="small"
                sx={{ 
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}
              >
                <ArrowBackIos fontSize="small" />
              </IconButton>
            )}
            
            <Typography variant="h6">
              {noteToEdit && noteToEdit.id ? 'Modifica Nota' : 'Nuova Nota'}
            </Typography>
            
            {showNavigation && (
              <IconButton 
                onClick={() => onNavigate(1)}
                size="small"
                sx={{ 
                  transition: 'all 0.3s ease',
                  '&:hover': { transform: 'scale(1.1)' }
                }}
              >
                <ArrowForwardIos fontSize="small" />
              </IconButton>
            )}
          </Box>
          
          {showNavigation && (
            <Typography variant="caption" color="text.secondary">
              {currentIndex + 1} di {totalNotes}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Fade in={true} timeout={300} key={`${noteToEdit?.id || 'new'}-${currentIndex}`}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            {/* Title Field */}
            <TextField
              name="title"
              label="Titolo"
              value={formData.title}
              onChange={handleInputChange}
              error={!!errors.title}
              helperText={errors.title || `${formData.title.length}/80 caratteri`}
              required
              fullWidth
              disabled={loading}
              inputProps={{ maxLength: 80 }}
            />

            {/* Date Field */}
            <TextField
              name="date"
              label="Data"
              type="date"
              value={formData.date}
              onChange={handleInputChange}
              error={!!errors.date}
              helperText={errors.date}
              required
              fullWidth
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
            />

            {/* Content Field */}
            <TextField
              name="content"
              label="Note"
              value={formData.content}
              onChange={handleInputChange}
              multiline
              rows={6}
              fullWidth
              disabled={loading}
              placeholder="Scrivi qui le tue note..."
              helperText="Puoi scrivere testo di lunghezza illimitata"
            />
          </Box>
        </Fade>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1, justifyContent: 'space-between' }}>
        <Box>
          {noteToEdit && noteToEdit.id && (
            <Button
              onClick={handleDelete}
              disabled={loading}
              startIcon={<DeleteIcon />}
              variant="outlined"
              color="error"
            >
              Elimina
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={handleCancel}
            disabled={loading}
            startIcon={<CancelIcon />}
            variant="outlined"
          >
            Annulla
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={loading || !formData.title.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            variant="contained"
            color="primary"
          >
            {loading ? 'Salvando...' : 'Salva'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default NoteDialog; 